import * as _ from 'lodash';
import {
  catchError,
  concat,
  distinctUntilChanged,
  map,
  Observable,
  of,
  startWith
} from 'rxjs';

import { Failure } from '@core/failure';
import { ObservableStatus } from '@shared/components/loading/models/observable-status';

/**
 * Custom rxJs operator that returns a function to convert observable of any type to observable of `ObservableStatus`.
 *
 * Since, this would be useful to identify the state of the observable if it is loading, completed with data or threw an error
 * @returns (o: Observable<T>) -> Observable<ObservableStatus<T>>
 */
export function updateWithObservableStatus<T>(): (
  source: Observable<T>
) => Observable<ObservableStatus<T>> {
  return (source: Observable<T>) => {
    return concat(of({ loading: true } as ObservableStatus<T>), source).pipe(
      map((x) => {
        if ('loading' in x) {
          return { loading: true } as ObservableStatus<T>;
        }
        return x instanceof Failure
          ? ({ error: x.error } as ObservableStatus<T>)
          : ({ data: x } as ObservableStatus<T>);
      }),
      startWith({ loading: true } as ObservableStatus<T>),
      catchError((e: any) => {
        return of({ error: e } as ObservableStatus<T>);
      })
    );
  };
}

/**
 * Custom rxJs operator that returns a function to identify if an object changed.
 *
 * Since, this would be useful not to trigger if not changed.
 * @returns (o: Observable<T>) => Observable<T>
 */

export function distinctUntilObjectChanged<T>(): (
  source: Observable<T>
) => Observable<T> {
  return (source: Observable<T>) => {
    return source.pipe(distinctUntilChanged((p, c) => _.isEqual(p, c)));
  };
}
